import { useState, useCallback, useRef, useEffect } from 'react'
import { router } from '@inertiajs/react'
import { Rock, RockCategory, User } from '../../types'
import RockProgressBar from './RockProgressBar'
import RockCheckInHistory from './RockCheckInHistory'
import MilestoneList from './MilestoneList'
import ConfirmModal from '../shared/ConfirmModal'
import RockPercentSlider from './RockPercentSlider'
import FloatingMenu from '../shared/FloatingMenu'
import { sortPeople } from '../../utils/peopleSort'
import { queueRockUpdate } from '../../utils/rockSaveQueue'

interface Props {
  /**
   * The rock being planned. Omit (or pass null) to open the planner in
   * create mode — an empty panel that creates the rock on its first autosave,
   * as soon as a Rock Name has been typed.
   */
  rock?: Rock | null
  teamId: number
  teamMembers: User[]
  categories?: RockCategory[]
  /** Pre-selects the owner in create mode. */
  currentUserId?: number
  onClose: () => void
  onCheckIn: (rock: Rock) => void
  /** Called once, with the new rock's id, after create mode's first save. */
  onCreated?: (rockId: number) => void
  onPrevRock?: () => void
  onNextRock?: () => void
}

type SaveStatus = 'idle' | 'dirty' | 'saving' | 'saved' | 'error'

type DirtyPayload = Record<string, string | number | null>

const AUTOSAVE_DELAY = 800

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
}

function mentionsOwner(errors: unknown): boolean {
  if (!errors || typeof errors !== 'object') return false
  return Object.entries(errors as Record<string, unknown>).some(([key, value]) =>
    key.toLowerCase().includes('owner') || String(value).toLowerCase().includes('owner'),
  )
}

function firstErrorMessage(errors: unknown): string {
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors as Record<string, unknown>)[0]
    if (typeof first === 'string' && first.length > 0) return first
    if (Array.isArray(first) && typeof first[0] === 'string' && first[0].length > 0) return first[0]
  }
  return "Couldn't save changes."
}

export default function RockPlanner({
  rock: rockProp, teamId, teamMembers, categories, currentUserId,
  onClose, onCheckIn, onCreated, onPrevRock, onNextRock,
}: Props) {
  // A rock created from here doesn't always show up in the list the parent
  // renders from: the team page's Backlog tab, a search, or a quarter filter
  // all exclude it, and so does a meeting whose Rock Review covers a different
  // quarter. So the panel fetches its own copy of the rock it created and
  // falls back to that whenever the parent can't supply one.
  const [fetchedRock, setFetchedRock] = useState<Rock | null>(null)
  const rock = rockProp ?? fetchedRock

  const isArchived = rock?.state === 'archived'
  const canDelete = rock?.can_delete === true

  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const dirtyFieldsRef = useRef<DirtyPayload>({})
  // Guards against a second create from rapid typing or an overlapping flush:
  // exactly one rock is created per panel session.
  const creatingRef = useRef(false)
  // The id is tracked in a ref as well as state because the debounced save can
  // fire before React has re-rendered with the freshly created rock.
  const rockIdRef = useRef<number | null>(rock?.id ?? null)
  const [rockId, setRockId] = useState<number | null>(rock?.id ?? null)
  const isNewRock = rockId === null

  const onCreatedRef = useRef(onCreated)
  useEffect(() => { onCreatedRef.current = onCreated }, [onCreated])

  // A member may create a rock for a teammate, but only the owner (or an admin)
  // may update one. Creating straight to that teammate would lock the creator
  // out of their own half-filled panel, so the handoff is held back: the rock is
  // created owned by its creator and the chosen owner is applied as the last
  // save of the session, when the panel closes.
  const createdHereRef = useRef(rockProp == null)
  const deferredOwnerRef = useRef<number | null>(null)
  // A creator who may not own rocks at all (an unprivileged guide) can't be the
  // placeholder owner; fall back to creating for the chosen owner directly.
  const ownerFallbackTriedRef = useRef(false)

  // Set once the panel is gone. A create can still be in flight then, and its
  // response must not reach back into the parent: the parent's binding state
  // outlives this panel, so a late `onCreated` would attach the *next* panel
  // session to this rock and turn its first save into an update of this one.
  const closedRef = useRef(false)

  // Responses can come back out of order; only the newest one may win.
  const fetchSeqRef = useRef(0)

  const fetchBoundRock = useCallback((id: number) => {
    if (typeof fetch !== 'function') return
    const seq = ++fetchSeqRef.current
    fetch(`/teams/${teamId}/rocks/${id}.json`, { // allow-direct-fetch: read-only GET to bind a newly created rock excluded from the parent list
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data?.rock && !closedRef.current && seq === fetchSeqRef.current) setFetchedRock(data.rock)
      })
      .catch(() => { /* the panel keeps working off local state */ })
  }, [teamId])

  // Keep the panel's own copy current after a save. Only the fetched copy needs
  // this — when the parent supplies the rock, its reload already refreshes it.
  // Draft inputs are never re-initialized from it, so this can't disturb typing.
  const refreshBoundRock = useCallback(() => {
    const id = rockIdRef.current
    if (id && !rockProp) fetchBoundRock(id)
  }, [rockProp, fetchBoundRock])

  // Bind to the rock the parent hands back after creation. Field state is
  // never re-initialized from props — the meeting page's background refresh
  // must not wipe in-progress text or a queued save.
  useEffect(() => {
    if (rock?.id && rock.id !== rockIdRef.current) {
      rockIdRef.current = rock.id
      setRockId(rock.id)
    }
  }, [rock?.id])

  const [status, setStatus] = useState<SaveStatus>('idle')
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Round: stored percent can carry fractional values (e.g. computed from a
  // target) that would fail the backend's 0.5-increment validation if echoed
  // back untouched. The slider works in whole numbers.
  const [percent, setPercent] = useState<number>(Math.round(rock?.percent_complete ?? 0))
  const percentSavesRef = useRef(0)
  useEffect(() => {
    if (!percentSavesRef.current && !('percent_complete' in dirtyFieldsRef.current)) {
      setPercent(Math.round(rock?.percent_complete ?? 0))
    }
  }, [rock?.percent_complete, status])
  const [isCompanyRock, setIsCompanyRock] = useState<boolean>(!!rock?.is_company_rock)
  const [title, setTitle] = useState(rock?.title || '')
  const [description, setDescription] = useState(rock?.description || '')
  const [whyImportant, setWhyImportant] = useState(rock?.why_important || '')
  const [resourcesNeeded, setResourcesNeeded] = useState(rock?.resources_needed || '')
  const [ownerId, setOwnerId] = useState(() => {
    if (rock) return rock.owner_id.toString()
    if (currentUserId && teamMembers.some(m => m.id === currentUserId)) return currentUserId.toString()
    return teamMembers[0]?.id?.toString() || ''
  })
  const [dueDate, setDueDate] = useState(rock?.due_date || '')
  const [categoryId, setCategoryId] = useState(rock?.category_id ? rock.category_id.toString() : '')
  const [progressType, setProgressType] = useState<string>(rock?.progress_type || 'status_only')
  const [startingValue, setStartingValue] = useState(rock?.starting_value != null ? String(rock.starting_value) : '')
  const [targetValue, setTargetValue] = useState(rock?.target_value != null ? String(rock.target_value) : '')
  const [targetType, setTargetType] = useState<string>(rock?.target_type || 'number')

  const [activeSection, setActiveSection] = useState<'planner' | 'history'>('planner')

  const [showActionMenu, setShowActionMenu] = useState(false)
  const actionMenuTriggerRef = useRef<HTMLButtonElement>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  // Mirrors deferredOwnerRef for rendering the handoff hint.
  const [ownerHandoffPending, setOwnerHandoffPending] = useState(false)

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      performSaveRef.current()
    }, AUTOSAVE_DELAY)
  }, [])

  const finishSave = useCallback(() => {
    // If the user typed more while the save was in flight, we're dirty again.
    if (Object.keys(dirtyFieldsRef.current).length > 0) {
      setStatus('dirty')
      scheduleSave()
    } else {
      setLastSavedAt(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
      setStatus('saved')
    }
  }, [scheduleSave])

  // Re-mark fields as dirty so a retry will resend them (unless the user has
  // already changed them again in the meantime).
  const failSave = useCallback((payload: DirtyPayload, message: string) => {
    for (const [k, v] of Object.entries(payload)) {
      if (!(k in dirtyFieldsRef.current)) dirtyFieldsRef.current[k] = v
    }
    setErrorMessage(message)
    setStatus('error')
  }, [])

  // Everything filled in so far — create sends the whole panel at once rather
  // than only the fields touched since the last save.
  const buildCreatePayload = useCallback((): DirtyPayload => {
    const payload: DirtyPayload = {
      title: title.trim(),
      description: description || null,
      why_important: whyImportant || null,
      resources_needed: resourcesNeeded || null,
      owner_id: deferredOwnerRef.current != null && currentUserId
        ? currentUserId
        : (ownerId ? Number(ownerId) : null),
      due_date: dueDate || null,
      category_id: categoryId ? Number(categoryId) : null,
      is_company_rock: isCompanyRock ? 1 : 0,
      progress_type: progressType,
    }
    if (progressType === 'status_with_target') {
      payload.starting_value = startingValue === '' ? null : Number(startingValue)
      payload.target_value = targetValue === '' ? null : Number(targetValue)
      payload.target_type = targetType
    }
    return payload
  }, [title, description, whyImportant, resourcesNeeded, ownerId, dueDate, categoryId,
      isCompanyRock, progressType, startingValue, targetValue, targetType])

  const performSave = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }

    const id = rockIdRef.current

    if (id === null) {
      // Create mode: hold edits locally until there's a Rock Name to save.
      if (creatingRef.current) return
      if (!title.trim()) return

      const payload = buildCreatePayload()
      dirtyFieldsRef.current = {}
      creatingRef.current = true
      setStatus('saving')
      setErrorMessage(null)

      router.post(`/teams/${teamId}/rocks`, { rock: payload }, {
        preserveScroll: true,
        preserveState: true,
        only: ['rocks', 'flash'],
        onSuccess: (page: any) => {
          const created = Number(page?.props?.flash?.created_rock_id)
          if (Number.isFinite(created) && created > 0) {
            rockIdRef.current = created
            setRockId(created)
            // Only tell the parent while this panel is still the open one.
            if (!closedRef.current) {
              onCreatedRef.current?.(created)
              fetchBoundRock(created)
            }
            finishSave()
            // The panel closed mid-create: flush whatever was typed after the
            // create went out, right away rather than on the debounce.
            if (closedRef.current && Object.keys(dirtyFieldsRef.current).length > 0) {
              creatingRef.current = false
              performSaveRef.current()
            }
          } else {
            failSave(payload, "Couldn't create this rock. Please try again.")
          }
        },
        onError: (errors) => {
          if (deferredOwnerRef.current != null && !ownerFallbackTriedRef.current && mentionsOwner(errors)) {
            ownerFallbackTriedRef.current = true
            deferredOwnerRef.current = null
            setOwnerHandoffPending(false)
            creatingRef.current = false
            performSaveRef.current()
            return
          }
          failSave(payload, firstErrorMessage(errors))
        },
        onFinish: () => { creatingRef.current = false },
      })
      return
    }

    const payload = dirtyFieldsRef.current
    if (Object.keys(payload).length === 0) return

    dirtyFieldsRef.current = {}
    setStatus('saving')
    setErrorMessage(null)

    // Through the shared queue: the planner autosaves while a row slider, a
    // sibling panel or the meeting's heartbeat may also be visiting, and
    // Inertia cancels whichever visit is in flight when a new one starts.
    const savesPercent = 'percent_complete' in payload
    if (savesPercent) percentSavesRef.current += 1
    queueRockUpdate(teamId, id, payload, {
      onSuccess: () => {
        if (savesPercent) percentSavesRef.current -= 1
        finishSave()
        refreshBoundRock()
      },
      onError: (_errors, message) => {
        if (savesPercent) percentSavesRef.current -= 1
        failSave(payload, message)
      },
    })
  }, [teamId, title, buildCreatePayload, finishSave, failSave, refreshBoundRock])

  // Keep latest performSave accessible to unmount cleanup without re-running effect.
  const performSaveRef = useRef(performSave)
  useEffect(() => { performSaveRef.current = performSave }, [performSave])

  const queueAutoSave = useCallback((field: string, value: string | number | null) => {
    dirtyFieldsRef.current[field] = value
    setErrorMessage(null)
    setStatus('dirty')
    scheduleSave()
  }, [scheduleSave])

  // On unmount, flush any pending edits so they aren't lost when the planner closes.
  useEffect(() => {
    return () => {
      releaseDeferredOwnerRef.current()
      closedRef.current = true
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      if (Object.keys(dirtyFieldsRef.current).length > 0) {
        // Fire-and-forget; the promise will resolve after unmount but the
        // request itself will reach the server.
        performSaveRef.current()
      }
    }
  }, [])

  const handleFieldChange = (field: string, value: string, setter: (v: string) => void) => {
    setter(value)
    queueAutoSave(field, value || null)
  }

  const handleOwnerChange = (value: string) => {
    setOwnerId(value)
    const nextOwner = Number(value)
    if (createdHereRef.current && currentUserId && nextOwner !== currentUserId) {
      // Hold the handoff until close, so the rest of the panel stays editable.
      deferredOwnerRef.current = nextOwner
      delete dirtyFieldsRef.current.owner_id
      setOwnerHandoffPending(true)
      return
    }
    deferredOwnerRef.current = null
    setOwnerHandoffPending(false)
    queueAutoSave('owner_id', nextOwner)
  }

  const handleCategoryChange = (value: string) => {
    setCategoryId(value)
    queueAutoSave('category_id', value ? Number(value) : null)
  }

  const handleProgressTypeChange = (value: string) => {
    setProgressType(value)
    queueAutoSave('progress_type', value)
    if (value === 'status_with_target') {
      queueAutoSave('starting_value', startingValue === '' ? null : Number(startingValue))
      queueAutoSave('target_value', targetValue === '' ? null : Number(targetValue))
      queueAutoSave('target_type', targetType)
    }
  }

  const handleNumberFieldChange = (field: string, value: string, setter: (v: string) => void) => {
    setter(value)
    queueAutoSave(field, value === '' ? null : Number(value))
  }

  const handleManualSave = () => {
    if (status === 'saving') return
    performSave()
  }

  // Last act of the session: hand ownership over, together with whatever else
  // is still pending, while this user can still update the rock.
  const releaseDeferredOwner = () => {
    const intended = deferredOwnerRef.current
    deferredOwnerRef.current = null
    if (intended == null) return
    // Before the rock exists the intended owner simply goes on the create.
    if (rockIdRef.current != null) dirtyFieldsRef.current.owner_id = intended
  }

  const releaseDeferredOwnerRef = useRef(releaseDeferredOwner)

  const handleClose = () => {
    releaseDeferredOwner()
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    if (Object.keys(dirtyFieldsRef.current).length > 0) {
      performSave()
    }
    onClose()
  }

  // Close drawer without flushing pending edits — used after destructive
  // actions (delete, archive, backlog/activate) where the rock is being
  // moved or removed and any in-flight unsaved field edits are moot.
  const closeWithoutFlush = useCallback(() => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    dirtyFieldsRef.current = {}
    onClose()
  }, [onClose])

  const handleStateAction = (action: 'backlog' | 'activate' | 'archive' | 'restore') => {
    setShowActionMenu(false)
    if (rockId === null) return
    const url = `/teams/${teamId}/rocks/${rockId}/${action === 'backlog' ? 'move_to_backlog' : action}`
    router.post(url, {}, {
      preserveScroll: true,
      onSuccess: () => closeWithoutFlush(),
    })
  }

  const handleDeleteClick = () => {
    setShowActionMenu(false)
    setDeleteError(null)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = () => {
    if (rockId === null) return
    setDeleting(true)
    setDeleteError(null)
    router.delete(`/teams/${teamId}/rocks/${rockId}`, {
      preserveScroll: true,
      onFinish: () => setDeleting(false),
      onSuccess: () => {
        setShowDeleteConfirm(false)
        closeWithoutFlush()
      },
      onError: () => setDeleteError("Couldn't delete this rock. Please try again."),
    })
  }

  const closeDeleteConfirm = () => {
    if (deleting) return
    setShowDeleteConfirm(false)
    setDeleteError(null)
  }

  const needsTitle = isNewRock && !title.trim()
  const hasDirty = status === 'dirty' || status === 'error'
  const saveButtonLabel =
    status === 'saving' ? 'Saving…' :
    status === 'error' ? 'Retry save' :
    status === 'saved' ? 'Saved' :
    status === 'idle' ? 'No changes' :
    'Save changes'
  const saveButtonDisabled =
    isArchived || needsTitle || status === 'saving' || status === 'saved' || status === 'idle'

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative bg-white w-full max-w-xl shadow-2xl overflow-y-auto flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-charcoal z-10">
          <div className="px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <svg className="w-5 h-5 text-pinnacle-red flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2L2 22h20L12 2zm0 4l7.5 14h-15L12 6z" />
                </svg>
                <span className="text-sm font-bold text-white tracking-wider uppercase font-heading truncate">
                  F.A.S.T. Rock Planner
                </span>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {!isNewRock && (onPrevRock || onNextRock) && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={onPrevRock}
                      disabled={!onPrevRock}
                      aria-label="Previous rock"
                      className="p-1.5 rounded hover:bg-white/10 text-text-on-dark disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={onNextRock}
                      disabled={!onNextRock}
                      aria-label="Next rock"
                      className="p-1.5 rounded hover:bg-white/10 text-text-on-dark disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                )}
                <SaveStatusPill status={status} lastSavedAt={lastSavedAt} />
                {!isNewRock && rock && (
                  <div className="relative">
                    <button
                      ref={actionMenuTriggerRef}
                      onClick={() => setShowActionMenu(s => !s)}
                      className="p-1.5 rounded hover:bg-white/10"
                      aria-label="Rock actions"
                      aria-haspopup="true"
                      aria-expanded={showActionMenu}
                    >
                      <svg className="w-5 h-5 text-text-on-dark" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>
                    <FloatingMenu
                      open={showActionMenu}
                      onClose={() => setShowActionMenu(false)}
                      anchorRef={actionMenuTriggerRef}
                      width={192}
                      className="bg-white rounded-lg shadow-xl border border-stone/20 py-1 w-48"
                    >
                          {rock.state === 'active' && (
                            <button
                              onClick={() => handleStateAction('backlog')}
                              className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-[#F5F5F5]/30"
                            >
                              Move to Backlog
                            </button>
                          )}
                          {rock.state === 'backlog' && (
                            <button
                              onClick={() => handleStateAction('activate')}
                              className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-[#F5F5F5]/30"
                            >
                              Activate Rock
                            </button>
                          )}
                          {rock.state !== 'archived' && (
                            <button
                              onClick={() => handleStateAction('archive')}
                              className="w-full text-left px-4 py-2 text-sm text-text-secondary hover:bg-[#F5F5F5]/30"
                            >
                              Archive
                            </button>
                          )}
                          {rock.state === 'archived' && (
                            <button
                              onClick={() => handleStateAction('restore')}
                              className="w-full text-left px-4 py-2 text-sm text-text-primary hover:bg-[#F5F5F5]/30"
                            >
                              Restore
                            </button>
                          )}
                          {canDelete && (
                            <>
                              <div className="border-t border-stone/10 my-1" />
                              <button
                                onClick={handleDeleteClick}
                                className="w-full text-left px-4 py-2 text-sm text-pinnacle-red hover:bg-[#F5F5F5]/30"
                              >
                                Delete Rock
                              </button>
                            </>
                          )}
                    </FloatingMenu>
                  </div>
                )}
                <button
                  onClick={handleClose}
                  className="p-1 rounded hover:bg-white/10"
                  aria-label="Close planner"
                >
                  <svg className="w-5 h-5 text-text-on-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {!isNewRock && (
            <div className="flex border-b border-white/10">
              <button
                onClick={() => setActiveSection('planner')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeSection === 'planner'
                    ? 'text-white border-b-2 border-pinnacle-red'
                    : 'text-text-on-dark hover:text-text-on-dark'
                }`}
              >
                Rock Plan
              </button>
              <button
                onClick={() => setActiveSection('history')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                  activeSection === 'history'
                    ? 'text-white border-b-2 border-pinnacle-red'
                    : 'text-text-on-dark hover:text-text-on-dark'
                }`}
              >
                Check-In History
              </button>
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {activeSection === 'planner' || isNewRock ? (
            <div className="p-5 space-y-5">
              {status === 'error' && errorMessage && (
                <div
                  role="alert"
                  className="flex items-start justify-between gap-3 p-3 rounded-lg border border-pinnacle-red/40 bg-pinnacle-red/10 text-sm text-text-primary"
                >
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 mt-0.5 text-pinnacle-red flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
                    </svg>
                    <div>
                      <div className="font-semibold">Couldn't save your changes</div>
                      <div className="text-text-muted">{errorMessage}</div>
                    </div>
                  </div>
                  <button
                    onClick={handleManualSave}
                    className="flex-shrink-0 px-3 py-1.5 bg-pinnacle-red text-white rounded-md text-xs font-semibold hover:bg-pinnacle-red/90"
                  >
                    Retry
                  </button>
                </div>
              )}

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => {
                    if (isArchived) return
                    const next = !isCompanyRock
                    setIsCompanyRock(next)
                    queueAutoSave('is_company_rock', next ? 1 : 0)
                  }}
                  disabled={isArchived}
                  aria-pressed={isCompanyRock}
                  title={isCompanyRock ? 'Click to make this a Personal Rock' : 'Click to mark as a Company Rock'}
                  className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                    isCompanyRock
                      ? 'bg-charcoal text-white hover:bg-charcoal/90'
                      : 'bg-charcoal/10 text-text-secondary hover:bg-charcoal/20'
                  } ${isArchived ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    {isCompanyRock ? (
                      <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" />
                    ) : (
                      <path d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" />
                    )}
                  </svg>
                  {isCompanyRock ? 'Company Rock' : 'Personal Rock'}
                </button>
                {rock?.quarter && (
                  <span className="text-xs text-text-muted font-medium">{rock.quarter}</span>
                )}
              </div>

              {!isNewRock && (
                <div>
                  <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5">
                    Percent Complete
                  </label>
                  <RockPercentSlider
                    value={percent}
                    onChange={setPercent}
                    onCommit={(v) => {
                      if (v !== rock?.percent_complete) {
                        queueAutoSave('percent_complete', v)
                        performSaveRef.current()
                      }
                    }}
                    disabled={isArchived}
                  />
                </div>
              )}

              {!isNewRock && rock && !isArchived && (
                <button
                  onClick={() => onCheckIn(rock)}
                  className="w-full py-2.5 bg-pinnacle-red text-white rounded-lg text-sm font-semibold hover:bg-pinnacle-red/90 font-heading"
                >
                  Check In
                </button>
              )}

              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-title">
                  Rock Name
                </label>
                <input
                  id="rock-planner-title"
                  type="text"
                  value={title}
                  onChange={e => handleFieldChange('title', e.target.value, setTitle)}
                  placeholder="e.g., Launch new customer portal"
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary font-semibold focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red"
                  disabled={isArchived}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-owner">
                    Owner
                  </label>
                  <select
                    id="rock-planner-owner"
                    aria-label="Owner"
                    value={ownerId}
                    onChange={e => handleOwnerChange(e.target.value)}
                    className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 bg-white"
                    disabled={isArchived}
                  >
                    {rock?.owner && !teamMembers.some(m => m.id === rock.owner?.id) && <option value={rock.owner.id} disabled>{rock.owner.full_name} (Current owner)</option>}
                    {sortPeople(teamMembers).map(m => (
                      <option key={m.id} value={m.id}>{m.full_name}</option>
                    ))}
                  </select>
                  {ownerHandoffPending && (
                    <p className="mt-1 text-xs text-text-muted">
                      You keep editing this rock until you close the planner, then it moves to this owner.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-due-date">
                    Due Date
                  </label>
                  <input
                    id="rock-planner-due-date"
                    type="date"
                    value={dueDate}
                    onChange={e => handleFieldChange('due_date', e.target.value, setDueDate)}
                    className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
                    disabled={isArchived}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-category">
                  Category
                </label>
                <select
                  id="rock-planner-category"
                  aria-label="Category"
                  value={categoryId}
                  onChange={e => handleCategoryChange(e.target.value)}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 bg-white"
                  disabled={isArchived}
                >
                  <option value="">No Category</option>
                  {(categories || []).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <span className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
                  Progress Type
                </span>
                {progressType === 'from_project' ? (
                  <p className="text-sm text-text-muted">Progress is tracked from the linked project.</p>
                ) : (
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="rock-planner-progress-type"
                        value="status_only"
                        checked={progressType === 'status_only'}
                        onChange={e => handleProgressTypeChange(e.target.value)}
                        className="text-pinnacle-red focus:ring-pinnacle-red"
                        disabled={isArchived}
                      />
                      <span className="text-sm text-text-primary">Status Only</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="rock-planner-progress-type"
                        value="status_with_target"
                        checked={progressType === 'status_with_target'}
                        onChange={e => handleProgressTypeChange(e.target.value)}
                        className="text-pinnacle-red focus:ring-pinnacle-red"
                        disabled={isArchived}
                      />
                      <span className="text-sm text-text-primary">Status with Target</span>
                    </label>
                  </div>
                )}
              </div>

              {progressType === 'status_with_target' && (
                <div className="bg-[#F5F5F5]/30 rounded-lg p-4 grid grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-starting-value">
                      Starting Value
                    </label>
                    <input
                      id="rock-planner-starting-value"
                      aria-label="Starting Value"
                      type="number"
                      value={startingValue}
                      onChange={e => handleNumberFieldChange('starting_value', e.target.value, setStartingValue)}
                      placeholder="0"
                      className="w-full px-3 py-1.5 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
                      disabled={isArchived}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-target-value">
                      Target Value
                    </label>
                    <input
                      id="rock-planner-target-value"
                      aria-label="Target Value"
                      type="number"
                      value={targetValue}
                      onChange={e => handleNumberFieldChange('target_value', e.target.value, setTargetValue)}
                      placeholder="100"
                      className="w-full px-3 py-1.5 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20"
                      disabled={isArchived}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-target-type">
                      Format
                    </label>
                    <select
                      id="rock-planner-target-type"
                      aria-label="Format"
                      value={targetType}
                      onChange={e => { setTargetType(e.target.value); queueAutoSave('target_type', e.target.value) }}
                      className="w-full px-3 py-1.5 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 bg-white"
                      disabled={isArchived}
                    >
                      <option value="number">Number</option>
                      <option value="currency">Currency ($)</option>
                      <option value="percentage">Percentage (%)</option>
                    </select>
                  </div>
                </div>
              )}

              {!isNewRock && rock && rock.progress_type === 'status_with_target' && (
                <div>
                  <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-2">
                    Progress
                  </label>
                  <RockProgressBar
                    percentage={rock.progress_percentage}
                    targetType={rock.target_type}
                    currentValue={rock.current_value}
                    targetValue={rock.target_value}
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-description">
                  What Does Done Look Like?
                </label>
                <textarea
                  id="rock-planner-description"
                  value={description}
                  onChange={e => handleFieldChange('description', e.target.value, setDescription)}
                  rows={3}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red resize-none"
                  placeholder="Describe the measurable outcome that defines completion..."
                  disabled={isArchived}
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-why-important">
                  Why Is This Important?
                </label>
                <textarea
                  id="rock-planner-why-important"
                  value={whyImportant}
                  onChange={e => handleFieldChange('why_important', e.target.value, setWhyImportant)}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red resize-none"
                  placeholder="How does this rock connect to broader goals?"
                  disabled={isArchived}
                />
              </div>

              <div className="border-t border-stone/10 pt-5">
                <MilestoneList
                  milestones={rock?.milestones || []}
                  teamId={teamId}
                  rockId={rockId ?? 0}
                  teamMembers={teamMembers}
                  disabled={isNewRock}
                  onChanged={refreshBoundRock}
                  onBeforeChange={() => performSaveRef.current()}
                  disabledHint="Add a rock name first — that saves the rock, then you can add milestones."
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-text-muted uppercase tracking-wider mb-1.5" htmlFor="rock-planner-resources">
                  Resources Needed
                </label>
                <textarea
                  id="rock-planner-resources"
                  value={resourcesNeeded}
                  onChange={e => handleFieldChange('resources_needed', e.target.value, setResourcesNeeded)}
                  rows={2}
                  className="w-full px-3 py-2 border border-stone/30 rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-pinnacle-red/20 focus:border-pinnacle-red resize-none"
                  placeholder="People, budget, tools, or support needed..."
                  disabled={isArchived}
                />
              </div>

              {!isNewRock && rock && (
                <div className="border-t border-stone/10 pt-4 text-xs text-text-muted">
                  Created {formatDate(rock.created_at)}
                  {rock.last_check_in_at && (
                    <span className="ml-3">Last check-in {formatDate(rock.last_check_in_at)}</span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="p-5">
              <RockCheckInHistory checkIns={rock?.check_ins || []} targetType={rock?.target_type ?? null} />
              {(rock?.check_ins?.length ?? 0) === 0 && (
                <p className="text-sm text-text-muted text-center py-8">No check-ins recorded yet.</p>
              )}
            </div>
          )}
        </div>

        {(activeSection === 'planner' || isNewRock) && !isArchived && (
          <div className="sticky bottom-0 bg-white border-t border-stone/30 px-5 py-3 flex items-center justify-between gap-3 shadow-[0_-2px_8px_rgba(53,30,29,0.06)]">
            <div className="text-xs text-text-muted">
              {needsTitle ? 'Add a rock name to save this rock.' : (
                <>
                  {status === 'saving' && 'Saving your changes…'}
                  {status === 'dirty' && 'You have unsaved changes.'}
                  {status === 'error' && <span className="text-pinnacle-red font-semibold">Save failed — retry to try again.</span>}
                  {status === 'saved' && (lastSavedAt ? `All changes saved at ${lastSavedAt}` : 'All changes saved.')}
                  {status === 'idle' && 'No changes yet.'}
                </>
              )}
            </div>
            <button
              onClick={handleManualSave}
              disabled={saveButtonDisabled}
              className={`px-4 py-2 rounded-lg text-sm font-semibold font-heading transition-colors ${
                saveButtonDisabled
                  ? 'bg-stone/40 text-text-muted cursor-not-allowed'
                  : 'bg-pinnacle-red text-white hover:bg-pinnacle-red/90'
              }`}
            >
              {hasDirty || status === 'saving' ? saveButtonLabel : 'Saved'}
            </button>
          </div>
        )}
      </div>

      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Rock"
        variant="danger"
        confirmLabel="Delete Rock"
        submitting={deleting}
        onClose={closeDeleteConfirm}
        onConfirm={confirmDelete}
        message={
          <>
            <p className="mb-2">
              Are you sure you want to delete <strong>{title}</strong>?
            </p>
            <p className="text-text-muted">
              This action is permanent and cannot be undone. All milestones and check-in history for this rock will also be removed.
            </p>
            {deleteError && (
              <p className="mt-3 px-3 py-2 bg-pinnacle-red/10 text-pinnacle-red rounded-lg" role="alert">
                {deleteError}
              </p>
            )}
          </>
        }
      />
    </div>
  )
}

function SaveStatusPill({ status, lastSavedAt }: { status: SaveStatus; lastSavedAt: string | null }) {
  const base = 'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold'

  if (status === 'saving') {
    return (
      <span className={`${base} bg-[#B87A20] text-white`}>
        <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-30" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
        </svg>
        Saving…
      </span>
    )
  }

  if (status === 'error') {
    return (
      <span className={`${base} bg-pinnacle-red text-white`}>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 9v2m0 4h.01M5.07 19h13.86c1.54 0 2.5-1.67 1.73-3L13.73 4a2 2 0 00-3.46 0L3.34 16c-.77 1.33.19 3 1.73 3z" />
        </svg>
        Couldn't save
      </span>
    )
  }

  if (status === 'dirty') {
    return (
      <span className={`${base} bg-white/15 text-white border border-white/30`}>
        <span className="w-1.5 h-1.5 rounded-full bg-white/80" />
        Unsaved changes
      </span>
    )
  }

  if (status === 'saved') {
    return (
      <span className={`${base} bg-[#15532E] text-white`} title={lastSavedAt ? `Saved at ${lastSavedAt}` : undefined}>
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
        </svg>
        Saved{lastSavedAt ? ` ${lastSavedAt}` : ''}
      </span>
    )
  }

  // idle — opened but not yet edited
  return (
    <span className={`${base} bg-white/10 text-text-on-dark border border-white/20`}>
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
      </svg>
      No changes
    </span>
  )
}
